import { Component, Inject, OnDestroy, Optional, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthenticationService } from '@app/authentication/services/authentication.service';
import { SnackbarService } from '@app/services/snackbar.sevice';
import { DateTime } from 'luxon';
import { ClinicalFeedbackConstants } from '../clinical-feedback-constants';
import { ClinicalFeedbackDetailsInjectData, SearchOrderResponse } from '../clinical-feedback.model';
import { ClinicalFeedbackService } from '../clinical-feedback.service';
import { DateAdapter } from '@angular/material/core';
import { DatePickerDateAdapter } from '@app/app-common/adapters/date-picker-date-adapter';
import { finalize, Subscription } from 'rxjs';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { NewClinicalFeedbackFormHelper } from './new-clinical-feedback.helper';
import { MessageBoxButtons, MessageBoxIcon, MessageBoxResult } from '@app/messagebox/messagebox.model';
import { MessageBoxService } from '@app/messagebox/services/messagebox.service';
import jsonpath from 'jsonpath';

@Component({
    selector: 'app-new-clinical-feedback',
    templateUrl: './new-clinical-feedback.component.html',
    styleUrls: ['./new-clinical-feedback.component.scss'],
    providers: [{ provide: DateAdapter, useClass: DatePickerDateAdapter }],
    encapsulation: ViewEncapsulation.None
})
export class NewClinicalFeedbackComponent implements OnDestroy {
    faCircleNotch = faCircleNotch;
    order!: SearchOrderResponse;
    isFromDialog = false;
    clinicalFeedbackForm = new UntypedFormGroup({});
    saving = false;
    private readonly subscription = new Subscription();

    constructor(
        private readonly router: Router,
        @Optional() public dialogRef: MatDialogRef<NewClinicalFeedbackComponent>,
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public data: ClinicalFeedbackDetailsInjectData,
        public authenticationService: AuthenticationService,
        public clinicalFeedbackService: ClinicalFeedbackService,
        public snackBarService: SnackbarService,
        private readonly messageBoxService: MessageBoxService
    ) {
        if (data) {
            this.isFromDialog = !!data.isFromDialog;
            this.order = data.order;
        }
    }

    async showConfirmationPopupIfDirty() {
        if (this.clinicalFeedbackForm.dirty) {
            this.subscription.add(
                this.messageBoxService
                    .showMessageBox({
                        title: 'Cancel Feedback Form',
                        message: 'All details entered will be lost.',
                        information: 'Are you sure you want to close this feedback form?',
                        icon: MessageBoxIcon.Information,
                        buttons: MessageBoxButtons.YesNo
                    })
                    .subscribe(async result => {
                        if (result === MessageBoxResult.Yes) {
                            this.closeForm(false);
                        }
                    })
            );
        } else {
            this.closeForm(false);
        }
    }

    private async closeForm(isSaved: boolean) {
        if (this.dialogRef) {
            this.dialogRef.close(isSaved);
        } else {
            await this.router.navigate([ClinicalFeedbackConstants.CLINICAL_FEEDBACK_DASHBOARD_PATH]);
        }
    }

    onSubmit(): void {
        if (this.clinicalFeedbackForm.valid) {
            this.saving = true;

            const form = this.clinicalFeedbackForm.value;
            const currentUser = this.authenticationService.getCurrentUser();
            form.contactDetails.userAddingFeedback = {
                name: currentUser.firstName + ' ' + currentUser.surname,
                email: currentUser.email,
                Phone: NewClinicalFeedbackFormHelper.GetFormattedPhoneNumber(currentUser.countryCode, currentUser.number),
                PhoneExt: currentUser.extension
            };
            this.transformData(form);
            this.clinicalFeedbackService
                .saveClinicalFeedback(this.order.accessionNumber, form.feedback.feedbackType, JSON.stringify(form))
                .pipe(
                    finalize(() => {
                        this.saving = false;
                    })
                )
                .subscribe({
                    next: response => {
                        this.closeForm(true);
                        this.snackBarService.openSnackBar(
                            `Clinical Feedback ${response.clinicalFeedbackId} was saved successfully`,
                            SnackbarService.SNACK_BAR_CLASS_SUCCESS,
                            SnackbarService.SNACK_BAR_DURATION_LONG
                        );
                    },
                    error: _ =>
                        this.snackBarService.openSnackBar(
                            'Clinical Feedback was not saved successfully',
                            SnackbarService.SNACK_BAR_CLASS_ALERT
                        )
                });
        } else {
            this.snackBarService.openSnackBar(
                'The feedback cannot be created. Please resolve the errors.',
                SnackbarService.SNACK_BAR_CLASS_ALERT
            );
            this.clinicalFeedbackForm.markAllAsTouched();
        }
    }

    transformData(data) {
        // Formatting date field to date string without timezone
        const dutyOfCandourDOMControl = this.clinicalFeedbackForm.get('advisedSeriousIncident')?.get('dutyOfCandour')?.get('dateOfMeeting');
        if (dutyOfCandourDOMControl?.valid && data.advisedSeriousIncident?.dutyOfCandour?.dateOfMeeting) {
            data.advisedSeriousIncident.dutyOfCandour.dateOfMeeting = DateTime.fromJSDate(dutyOfCandourDOMControl.value).toISODate();
        }
        const confirmedSeriousIncidentDOMControl = this.clinicalFeedbackForm
            .get('advisedSeriousIncident')
            ?.get('confirmedSeriousIncident')
            ?.get('dateOfMeeting');
        if (confirmedSeriousIncidentDOMControl?.valid && data.advisedSeriousIncident?.confirmedSeriousIncident?.dateOfMeeting) {
            data.advisedSeriousIncident.confirmedSeriousIncident.dateOfMeeting = DateTime.fromJSDate(
                confirmedSeriousIncidentDOMControl.value
            ).toISODate();
        }

        const decodeHtmlSpaces = (value: string) => (value ? value.replace(/&nbsp;/g, ' ') : value);
        [
            '$.feedback.clinicalImpactDetails',
            '$.feedback.feedbackDetails',
            '$.addendum.addendumDetails',
            '$..supportFromEverlight',
            '$..detailsOfDiscrepancy',
            '$..lessonsLearnt'
        ].forEach(path => {
            jsonpath.apply(data, path, decodeHtmlSpaces);
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
